/**
 * API Keys Management Page
 *
 * Allows users to create, view, and revoke API keys for MCP access.
 */

import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import { getAccessToken } from '../lib/supabase';

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt?: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.aigentswork.com';

async function fetchApiKeys(): Promise<ApiKey[]> {
  const token = await getAccessToken();
  if (!token) return [];

  const response = await fetch(`${API_BASE}/api/api-keys`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) return [];
  return response.json();
}

const ApiKeys: Component = () => {
  const [keys, { refetch }] = createResource(fetchApiKeys);
  const [showCreate, setShowCreate] = createSignal(false);
  const [newKeyName, setNewKeyName] = createSignal('');
  const [newKeyScopes, setNewKeyScopes] = createSignal<string[]>(['read']);
  const [createdKey, setCreatedKey] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  const createApiKey = async () => {
    setError(null);
    setLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/api-keys`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newKeyName(),
          scopes: newKeyScopes(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create API key');
      }

      const data = await response.json();
      setCreatedKey(data.key);
      setNewKeyName('');
      setShowCreate(false); // Close the modal
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (id: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/api-keys/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to revoke API key');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    }
  };

  const toggleScope = (scope: string) => {
    const current = newKeyScopes();
    if (current.includes(scope)) {
      setNewKeyScopes(current.filter((s) => s !== scope));
    } else {
      setNewKeyScopes([...current, scope]);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div>
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold">API Keys</h1>
          <p class="text-gray-400 mt-1">Manage API keys for MCP and programmatic access</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          class="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg shadow-purple-500/20"
        >
          Create API Key
        </button>
      </div>

      {/* Error Message */}
      <Show when={error()}>
        <div class="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
          <p class="text-red-400">{error()}</p>
        </div>
      </Show>

      {/* Created Key Banner */}
      <Show when={createdKey()}>
        <div class="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl">
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-green-500 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div class="flex-1">
              <p class="text-green-400 font-medium">API Key Created Successfully</p>
              <p class="text-gray-400 text-sm mt-1">
                Copy this key now. You won't be able to see it again.
              </p>
              <div class="mt-3 flex items-center gap-2">
                <code class="flex-1 px-3 py-2 bg-gray-800 rounded-lg font-mono text-sm text-gray-300 overflow-x-auto">
                  {createdKey()}
                </code>
                <button
                  onClick={() => copyToClipboard(createdKey()!)}
                  class="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
            <button onClick={() => setCreatedKey(null)} class="text-gray-500 hover:text-gray-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </Show>

      {/* Create Key Modal */}
      <Show when={showCreate()}>
        <div
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCreate(false)}
        >
          <div
            class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 class="text-xl font-bold mb-4">Create API Key</h2>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">Name</label>
                <input
                  type="text"
                  value={newKeyName()}
                  onInput={(e) => setNewKeyName(e.currentTarget.value)}
                  placeholder="My API Key"
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Permissions</label>
                <div class="space-y-2">
                  <For each={['read', 'write', 'delete', 'admin']}>
                    {(scope) => (
                      <label class="flex items-center gap-3 p-3 bg-gray-700/30 rounded-lg cursor-pointer hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={newKeyScopes().includes(scope)}
                          onChange={() => toggleScope(scope)}
                          class="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                        />
                        <span class="capitalize">{scope}</span>
                      </label>
                    )}
                  </For>
                </div>
              </div>
            </div>

            <div class="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreate(false)}
                class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createApiKey}
                disabled={loading() || !newKeyName().trim()}
                class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {loading() ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* API Keys List */}
      <div class="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
        <Show
          when={!keys.loading}
          fallback={
            <div class="p-8 text-center">
              <svg
                class="animate-spin w-8 h-8 text-purple-500 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          }
        >
          <Show
            when={keys()?.length}
            fallback={
              <div class="p-8 text-center">
                <svg
                  class="w-12 h-12 text-gray-600 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
                <p class="text-gray-400">No API keys yet</p>
                <p class="text-gray-500 text-sm mt-1">Create your first API key to get started</p>
              </div>
            }
          >
            <table class="w-full">
              <thead>
                <tr class="border-b border-gray-700">
                  <th class="text-left px-4 py-3 text-sm font-medium text-gray-400">Name</th>
                  <th class="text-left px-4 py-3 text-sm font-medium text-gray-400">Key</th>
                  <th class="text-left px-4 py-3 text-sm font-medium text-gray-400">Scopes</th>
                  <th class="text-left px-4 py-3 text-sm font-medium text-gray-400">Created</th>
                  <th class="text-right px-4 py-3 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                <For each={keys()}>
                  {(key) => (
                    <tr class="border-b border-gray-700/50 hover:bg-gray-700/20">
                      <td class="px-4 py-3 font-medium">{key.name}</td>
                      <td class="px-4 py-3">
                        <code class="text-gray-400 font-mono text-sm">{key.keyPrefix}...</code>
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex gap-1 flex-wrap">
                          <For each={key.scopes}>
                            {(scope) => (
                              <span class="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded">
                                {scope}
                              </span>
                            )}
                          </For>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-gray-400 text-sm">
                        {new Date(key.createdAt).toLocaleDateString()}
                      </td>
                      <td class="px-4 py-3 text-right">
                        <button
                          onClick={() => revokeKey(key.id)}
                          class="text-red-400 hover:text-red-300 text-sm"
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </Show>
      </div>
    </div>
  );
};

export default ApiKeys;
